import { lazy, ComponentType } from 'react';
import type { VisualizationVersion } from '../visualizationVersion';
import RowingArcWrapper from './wrappers/RowingArcWrapper';

/**
 * Supported contract types for graph data
 */
export type GraphContractType = 'PowerTimeSeries' | 'GateForceProfile' | null;

/**
 * Standard props that all graph components must accept
 */
export interface GraphComponentProps {
  sessionId: string | null;
  parameterInfo?: any;
  className?: string;
  /** Optional rower context for rower-focused visualizations */
  rowerContext?: import('../../types/rowerContext').RowerContext | null;
  /** Concept2 Logbook workout id when the graph consumes erg stroke data */
  concept2WorkoutId?: string | null;
  /**
   * When true (default), the graph is shown inside a fixed-height tile (builder / saved grid) and
   * should fill the tile and scroll inside when needed. When false, height follows page content.
   */
  tileMode?: boolean;
}

export interface GraphRegistryMeta {
  renderer?: 'd3' | 'chartjs' | 'three' | 'other';
  author?: string;
  [key: string]: unknown;
}

/**
 * Graph registry entry definition
 */
export interface GraphRegistryEntry {
  /** Unique identifier matching feature ID */
  id: string;
  /** Display name for the graph */
  name: string;
  /** Description of what the graph displays */
  description: string;
  /** Semantic version of the shipped visualization implementation */
  version: VisualizationVersion;
  /** Contract type this graph consumes (null if no contract needed) */
  contractType: GraphContractType;
  /** Lazy-loaded React component */
  component: ComponentType<GraphComponentProps>;
  /** Optional category for organization */
  category?: 'power' | 'force' | 'stroke' | 'general';
  /**
   * When false, the graph stays in the catalog but is hidden from the coach dashboard builder
   * until a surface can supply the required data context (e.g. Concept2 workout selection).
   */
  dashboardBuilderEligible?: boolean;
  /** Optional implementation metadata */
  meta?: GraphRegistryMeta;
}

/**
 * Central graph registry
 * This is the single source of truth for all available graphs
 */
export const GRAPH_REGISTRY: Record<string, GraphRegistryEntry> = {
  'power-analysis': {
    id: 'power-analysis',
    name: 'Power Analysis',
    description: 'Per-stroke power output visualization with waveform display',
    version: '2.0.0',
    contractType: 'PowerTimeSeries',
    component: lazy(() => import('./wrappers/PowerAnalysisWrapper')),
    category: 'power',
  },

  'performance-metrics': {
    id: 'performance-metrics',
    name: 'Performance Metrics',
    description: 'Individual rower power comparison and crew analysis',
    version: '2.0.0',
    contractType: 'PowerTimeSeries',
    // D3.js implementation with built-in ChartContainer, table, and overlay toggles
    component: lazy(() => import('./wrappers/PerformanceMetricsWrapper')),
    category: 'power',
    meta: {
      renderer: 'd3',
    },
  },

  'gate-force': {
    id: 'gate-force',
    name: 'Gate Force Signature',
    description:
      'D3 visualization of gate force patterns showing force application curves for each stroke',
    version: '3.0.0',
    contractType: 'GateForceProfile',
    component: lazy(() => import('./wrappers/GateForceWrapper')),
    category: 'force',
    meta: {
      renderer: 'd3',
    },
  },

  'per-stroke': {
    id: 'per-stroke',
    name: 'Per-Stroke Summary',
    description: 'Detailed stroke-by-stroke analysis and metrics',
    version: '1.0.0',
    contractType: null, // Uses its own data fetching for now
    component: lazy(() => import('../../components/coach/charts/per-stroke/SummaryPerStrokeChart')),
    category: 'stroke',
  },

  'rowing-arc': {
    id: 'rowing-arc',
    name: 'Rowing Arc',
    description: 'Catch/finish arc geometry and per-seat stroke profile visualization',
    version: '1.0.0',
    contractType: null, // Uses its own telemetry query path
    // Intentionally eager to avoid double-loading UX (registry lazy + data fetch loader).
    component: RowingArcWrapper,
    category: 'stroke',
    meta: {
      renderer: 'd3',
    },
  },

  'concept2-workout-analysis': {
    id: 'concept2-workout-analysis',
    name: 'Concept2 Workout Analysis',
    description:
      'ErgData-inspired pace, stroke rate, and heart rate charts for Concept2 Logbook workouts',
    version: '1.0.0',
    contractType: null,
    component: lazy(() => import('./wrappers/Concept2WorkoutAnalysisWrapper')),
    category: 'general',
    dashboardBuilderEligible: false,
    meta: {
      renderer: 'd3',
      dataSource: 'concept2-workout',
    },
  },
};

/**
 * Helper: Get graph entry by ID
 */
export const getGraphEntry = (graphId: string): GraphRegistryEntry | null => {
  return GRAPH_REGISTRY[graphId] || null;
};

/**
 * Helper: Get all graph IDs
 */
export const getGraphIds = (): string[] => {
  return Object.keys(GRAPH_REGISTRY);
};

/**
 * Helper: Get graphs by category
 */
export const getGraphsByCategory = (
  category: 'power' | 'force' | 'stroke' | 'general'
): GraphRegistryEntry[] => {
  return Object.values(GRAPH_REGISTRY).filter((entry) => entry.category === category);
};

/**
 * Helper: Get graphs by contract type
 */
export const getGraphsByContract = (contractType: GraphContractType): GraphRegistryEntry[] => {
  return Object.values(GRAPH_REGISTRY).filter((entry) => entry.contractType === contractType);
};

export function isGraphDashboardBuilderEligible(graphId: string): boolean {
  const entry = getGraphEntry(graphId);
  if (!entry) {
    return true;
  }

  return entry.dashboardBuilderEligible !== false;
}
